import { useState, useEffect, useRef } from 'react';
import { RelationFieldType } from 'src/Types/Form';
import { EnumItemType, EnumsType } from '../';
import API from 'src/API';
import { SelectSimple, type SelectSimpleProps } from '@redoc/ui-kit';

export interface RelationFieldProps extends React.HTMLAttributes<HTMLDivElement> {
	isPlaceholder?: boolean;
	placeholder?: string;
	disabled?: boolean;
	searchEnable?: boolean;
	showClearBtn?: boolean;

	value?: string | Array<string>;
	/** Уже примененное значение, которое должно отображаться в начале списка */
	appliedValue?: string | Array<string> | null;
	setValue?: React.Dispatch<React.SetStateAction<string | Array<string>>>;
	onClear?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

	onClose?: () => void;
	/** Вызывается после завершения первой загрузки опций */
	onFirstLoadComplete?: () => void;
	onStateChange?: (type: string, props: any) => void;

	minSearchLength?: number;
	onSearch?: (tern: string) => void;

	relation: RelationFieldType;
	onPrepareData?: (result: any) => EnumsType;

	multiple?: boolean;
	defaultOpen?: boolean;
	isOwnerList?: boolean;
	isBlockArrow?: boolean;
	isPortal?: boolean;
	/** Уникальное имя селекта для идентификаторов опций */
	name?: SelectSimpleProps['name'];
	/** Режим отображения списка SelectSimple */
	listMode?: SelectSimpleProps['listMode'];
	/** Область действий под списком опций */
	footer?: React.ReactNode;
}

/** Компонента выпадающего списка с серверным запросом опций */
const RelationField: React.FC<RelationFieldProps> = (props) => {
	const { onFirstLoadComplete, ...selectProps } = props;
	const {
		relation,
		onPrepareData,
		value,
		appliedValue,
		minSearchLength = 3,
	} = selectProps;
	const isInline = selectProps.listMode === 'inline';

	const [isWait, setIsWait] = useState<boolean>(false);
	const [data, setData] = useState<EnumsType>([]);
	const [search, setSearch] = useState<string>('');
	/** Не позволяет повторным поискам повторно вызвать callback первой загрузки */
	const isFirstLoadCompleted = useRef(false);

	useEffect(() => {
		loadOptions(search);
		// eslint-disable-next-line
	}, [search]);

	/** Функция получения списка опций */
	const loadOptions = (searchText: string) => {
		setIsWait(true);
		const adddata = relation.onPrepareRequestData
			? relation.onPrepareRequestData()
			: null;
		API.getFilterValues(
			relation.method,
			relation.url,
			searchText,
			0,
			100,
			value,
			adddata,
		)
			.then((result) => {
				if (!result?.hasError) {
					const prepared = onPrepareData
						? onPrepareData
						: prepareEnumData;
					// Тут аццкий говонокод( костыль для отображения ранее выбранных значений при фильтрации
					setData((prev) => {
						const list = prepared(result, relation);
						if (
							value &&
							!list.filter((op) => {
								return isSelectedOption(op, value);
							}).length
						) {
							const selectedOptions = prev.filter((op) => {
								return isSelectedOption(op, value);
							});
							list.push(...selectedOptions);
						}
						return moveAppliedOptionsFirst(list, appliedValue);
					});
				}
			})
			.catch((e: any) => {
				console.warn(e);
			})
			.finally(() => {
				setIsWait(false);
				if (!isFirstLoadCompleted.current) {
					isFirstLoadCompleted.current = true;
					onFirstLoadComplete?.();
				}
			});
	};

	/** Показывает дефолтные опции до ввода минимальной длины поиска */
	const onSearchHandler = (text: string) => {
		const searchText =
			isInline && text.length < minSearchLength ? '' : text;
		return setSearch(searchText);
	};

	const setValueHandler = (val: string | number | Array<string | number>) => {
		if (props.setValue) {
			const stringValue = Array.isArray(val)
				? val.map((v) => {
						return String(v);
					})
				: String(val);
			props.setValue(stringValue as string | Array<string>);
		}
	};

	return (
		<SelectSimple
			{...selectProps}
			setValue={setValueHandler}
			options={data}
			isWait={isWait}
			onSearch={relation.serverSideSearch ? onSearchHandler : undefined}
			searchEnable={relation.serverSideSearch}
			minSearchLength={isInline ? 0 : minSearchLength}
		/>
	);
};

/** Возвращает ИД опции */
const getIdOption = (option: EnumItemType): string => {
	return typeof option === 'object' && option?.id
		? option.id.toString()
		: option.toString();
};

/** Функция проверки опции на признак выбраности */
const isSelectedOption = (
	option: EnumItemType,
	values: string | string[],
): boolean => {
	if (Array.isArray(values)) {
		return values.includes(getIdOption(option));
	} else {
		return getIdOption(option) === values;
	}
};

/** Перемещает уже примененные relation-опции в начало списка */
const moveAppliedOptionsFirst = (
	options: EnumsType,
	appliedValue?: string | Array<string> | null,
): EnumsType => {
	let appliedValues: string[] = [];
	if (Array.isArray(appliedValue)) {
		appliedValues = appliedValue;
	} else if (appliedValue) {
		appliedValues = [appliedValue];
	}

	if (!appliedValues.length) {
		return options;
	}

	const appliedIds = new Set(appliedValues);
	const isAppliedOption = (option: EnumItemType) => {
		return appliedIds.has(getIdOption(option));
	};

	return [
		...options.filter(isAppliedOption),
		...options.filter((option) => {
			return !isAppliedOption(option);
		}),
	];
};

const prepareEnumData = (
	data: any = [],
	relation: RelationFieldType,
): EnumsType => {
	return data.map((item: any) => {
		return {
			id: item[relation.valueKey],
			title: relation.titleKey
				? item[relation.titleKey]
				: item[relation.valueKey],
		};
	});
};

export default RelationField;
